import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { productsTranslations } from '../data/productsData';
import { servicesTranslations } from '../data/translations/servicesTranslations';
import { useLanguage } from '../context/LanguageContext';
import { usePortfolioFilter } from '../context/PortfolioFilterContext';

// Highlights: wrap terms in [[ ]] to render them in text-secondary font-semibold
const categoryDescriptions = {
  Imagenologia: {
    es: "más de 100 soluciones de apoyo diagnóstico para distintas modalidades, como [[Resonancia Magnética, Tomografía Computada, Mamografías, Ultrasonido y Rayos X]], integradas a los flujos clínicos habituales.",
    en: "Over 100 diagnostic support solutions across modalities including [[MRI, CT, Mammography, Ultrasound, and X-Ray]], integrated into standard clinical workflows.",
  },
  Dermatologia: {
    es: "soluciones de apoyo para la evaluación de lesiones cutáneas, con capacidad de identificar [[más de 300 hallazgos dermatológicos]], contribuyendo a una detección más oportuna y estandarizada.",
    en: "Support solutions for skin lesion assessment, capable of identifying [[over 300 dermatological findings]], contributing to earlier and more standardized detection.",
  },
  Cardiologia: {
    es: "soluciones de apoyo para el análisis cardiovascular en estudios como [[Holter, Electrocardiograma y Ultrasonido]], que pueden contribuir a una mejor priorización, detección y seguimiento clínico.",
    en: "Support solutions for cardiovascular analysis across studies including [[Holter, ECG, and Ultrasound]], contributing to better prioritization, detection, and clinical follow-up.",
  },
  "Salud Mental": {
    es: "soluciones basadas en inteligencia artificial para apoyar el análisis en áreas [[cognitivas y conductuales]], facilitando una evaluación complementaria, objetiva y escalable.",
    en: "AI-based solutions to support analysis in [[cognitive and behavioral]] areas, enabling a complementary, objective, and scalable assessment.",
  },
  "Laboratorio Clinico": {
    es: "soluciones que fortalecen la interpretación y gestión de datos de laboratorio, con capacidad de analizar [[más de 1.000 biomarcadores]], ayudando a optimizar procesos diagnósticos y la toma de decisiones.",
    en: "Solutions that strengthen the interpretation and management of laboratory data, capable of analyzing [[over 1,000 biomarkers]], helping optimize diagnostic processes and decision-making.",
  },
};

export default function Servicios() {
  const { language } = useLanguage();
  const { activeCategory, setActiveCategory } = usePortfolioFilter();
  const d = productsTranslations[language];
  const t = servicesTranslations[language].portfolio;

  const allCategories = [...new Set(d.items.map(p => p.category))];
  const categories = ['Imagenologia', ...allCategories.filter(c => c !== 'Imagenologia')];
  const translatedCategories = d.categories;

  const activeDescription = categoryDescriptions[activeCategory] ?? null;

  return (
    <div className="bg-background text-on-surface font-body overflow-hidden" id="servicios">
      <section className="py-24 px-8 bg-surface">
        <div className="max-w-7xl mx-auto">

          {/* Header */}
          <div className="mb-16 text-center lg:text-left flex flex-col lg:flex-row justify-between items-end gap-8">
            <div className="max-w-2xl">
              <h2 className="font-headline text-4xl lg:text-5xl font-extrabold text-primary leading-tight text-balance">
                {t.title} <span className="text-secondary italic">{t.subtitle}</span>
              </h2>
              <p className="mt-6 opacity-60 text-lg leading-relaxed text-balance">
                {t.description}
              </p>
            </div>
          </div>

          {/* Filter Bar */}
          <div className="mb-12 overflow-x-auto pb-4 -mx-8 px-8 no-scrollbar">
            <div className="flex flex-nowrap md:flex-wrap items-center gap-3">
              {categories.map((cat) => (
                <button
                  key={cat}
                  onClick={() => setActiveCategory(cat)}
                  className={`whitespace-nowrap px-6 py-2.5 rounded-full text-xs font-bold tracking-wider transition-all duration-300 border ${
                    activeCategory === cat
                      ? 'bg-primary text-on-primary border-primary shadow-lg shadow-secondary/10 scale-105'
                      : 'bg-surface text-primary/60 border-border hover:border-secondary hover:bg-background'
                  }`}
                >
                  {translatedCategories[cat] || cat}
                </button>
              ))}
            </div>
          </div>

          {/* Category Description Box */}
          <div className="min-h-[200px] flex items-center">
            <AnimatePresence mode="wait">
              {activeDescription ? (
                <motion.div
                  key={activeCategory}
                  initial={{ opacity: 0, y: 16 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -16 }}
                  transition={{ duration: 0.35, ease: 'easeOut' }}
                  className="w-full p-10 bg-background border border-border rounded-2xl flex flex-col md:flex-row items-start md:items-center gap-8"
                >
                  <div className="flex-1">
                    <h3 className="font-headline text-2xl font-bold text-primary mb-3">
                      {translatedCategories[activeCategory] || activeCategory}
                    </h3>
                    <p className="text-on-surface opacity-60 text-base leading-relaxed max-w-2xl">
                      {activeDescription[language].split('[[').map((part, i) => {
                        if (part.includes(']]')) {
                          const [highlighted, rest] = part.split(']]');
                          return (
                            <React.Fragment key={i}>
                              <span className="text-secondary font-semibold opacity-100">{highlighted}</span>
                              {rest}
                            </React.Fragment>
                          );
                        }
                        return part;
                      })}
                    </p>
                  </div>
                </motion.div>
              ) : (
                <motion.div
                  key="todos"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.25 }}
                  className="w-full p-10 bg-surface/50 border-2 border-dashed border-border rounded-2xl flex items-center justify-center"
                >
                  <p className="text-xs font-bold uppercase tracking-[0.2em] text-muted opacity-30">
                    {language === 'es' ? 'Selecciona una categoría' : 'Select a category'}
                  </p>
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          {/* Vendor Neutral Note */}
          <div className="mt-20 p-8 rounded-[2rem] bg-surface text-on-surface flex flex-col md:flex-row items-center justify-between gap-8 border border-border shadow-2xl overflow-hidden relative group">
            <div className="absolute top-0 right-0 w-64 h-64 bg-secondary/10 blur-[100px] rounded-full -translate-y-1/2 translate-x-1/2 group-hover:bg-secondary/20 transition-all duration-700"></div>
            <div className="relative z-10 flex-grow max-w-2xl">
              <h3 className="font-headline text-2xl font-bold mb-4">{t.sovereignty.title}</h3>
              <p className="opacity-60 text-sm leading-relaxed">
                {t.sovereignty.text}
              </p>
            </div>
            <div className="relative z-10 flex-shrink-0 flex gap-4">
              <div className="text-right">
                <div className="text-3xl font-black text-secondary">{t.sovereignty.subtitle}</div>
                <div className="text-[10px] font-bold opacity-40 uppercase tracking-widest">{t.sovereignty.guarantee}</div>
              </div>
            </div>
          </div>

        </div>
      </section>
    </div>
  );
}
